import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, importProvidersFrom, provideZoneChangeDetection } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { provideRouter } from "@angular/router";
import { routes } from "./app.routes";
import { provideTranslateService } from "@ngx-translate/core";
import { provideHttpClient, withInterceptorsFromDi, withXhr } from "@angular/common/http";
import { OAuthModule } from "angular-oauth2-oidc";
import { provideStoreDevtools } from "@ngrx/store-devtools";
import { environment } from "../environments/environment";
import { provideAnimations } from "@angular/platform-browser/animations";
import { provideTranslateHttpLoader } from "@ngx-translate/http-loader";
import { AuthService } from "./core/auth/auth.service";
import { GlobalErrorHandler } from "./core/globalErrorHandler";
import { RemainingDaysPipe } from "./shared/pipes/remaining-days.pipe";

//Auth-Service beim App-Start initialisieren
function initializeAuthService(authService: AuthService): () => Promise<void> {
    return () => authService.bootstrapOAuthService();
}

/**
 * Anwendungsweite Konfiguration fuer Angulars Dependency-Injection-System.
 *
 * @remarks
 * Registriert die zentralen Provider der Anwendung:
 * - Zone Change Detection mit Event-Coalescing.
 * - Angular Router mit den App-Routen.
 * - Uebersetzungs-Service (ngx-translate) mit HTTP-Loader und Englisch als Fallback.
 * - FormsModule und ReactiveFormsModule.
 * - OAuthModule fuer den sicheren Zugriff auf das Backend (PKCE).
 * - APP_INITIALIZER fuer den OAuth-Bootstrap.
 * - GlobalErrorHandler als zentraler Fehlerhandler.
 * - ngrx Store DevTools.
 * - HTTP-Client mit DI-basierten Interceptors.
 * - Animations-Provider.
 *
 * @see {@link ApplicationConfig}
 */
export const appConfig: ApplicationConfig = {
    providers: [
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideRouter(routes),
        provideTranslateService({
            fallbackLang: 'en',
            loader: provideTranslateHttpLoader()
        }),
        importProvidersFrom(
            FormsModule,
            ReactiveFormsModule,
            OAuthModule.forRoot({
                resourceServer: {
                    allowedUrls: [
                        'https://fa-bihler-digital-backend-dev.azurewebsites.net/api',
                        'https://fa-bihler-digital-backend-prod.azurewebsites.net/api',
                        'https://fa-machine-cockpit-dev.azurewebsites.net/api',
                        'https://fa-machine-cockpit-prod.azurewebsites.net/api'
                    ],
                    sendAccessToken: true
                }
            })
        ),
        {
            provide: APP_INITIALIZER,
            deps: [AuthService],
            useFactory: initializeAuthService,
            multi: true
        },
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler
        },
        RemainingDaysPipe,
        provideStoreDevtools({
            name: 'bNet App DevTools',
            maxAge: 25,
            logOnly: environment.production
        }),
        provideHttpClient(withXhr(), withInterceptorsFromDi()),
        provideAnimations()
    ]
};
